import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { PayrollService } from '../../services/payroll.service';
import { IEmployeeSalary, ISalaryComponent } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import EmployeeSalaryForm from './EmployeeSalaryForm';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { displayUsername } from '../../utils/userUtils';
import SearchableSelect from '../common/SearchableSelect';

const EmployeeSalaryList: React.FC = () => {
    const [salaries, setSalaries] = useState<IEmployeeSalary[]>([]);
    const [components, setComponents] = useState<ISalaryComponent[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSalary, setEditingSalary] = useState<IEmployeeSalary | null>(null);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const loadData = async () => {
        if (!institutionId) return;
        try {
            const [salaryData, componentData] = await Promise.all([
                PayrollService.getEmployeeSalaries(institutionId),
                PayrollService.getSalaryComponents(institutionId)
            ]);
            setSalaries(salaryData);
            setComponents(componentData);
        } catch (error) {
            showApiError(error, 'Failed to load data');
        }
    };

    useEffect(() => { loadData(); }, [institutionId]);

    const handleAdd = () => {
        setEditingSalary(null);
        setDialogOpen(true);
    };

    const handleEdit = (salary: IEmployeeSalary) => {
        setEditingSalary(salary);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        setDialogOpen(false);
        showSuccess('Employee salary saved');
        loadData();
    };

    const getTotalSalary = (salary: IEmployeeSalary) => {
        const earnings = salary.details
            .filter(d => d.componentType === 'EARNING')
            .reduce((sum, d) => sum + d.amount, 0);
        const deductions = salary.details
            .filter(d => d.componentType === 'DEDUCTION')
            .reduce((sum, d) => sum + d.amount, 0);
        return { earnings, deductions, net: earnings - deductions };
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.salaries.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Employee Salaries</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {isSystemAdmin && (
                        <SearchableSelect
                            data-help="payroll.salaries.institution"
                            label="Select Institution"
                            options={institutions}
                            value={institutionId}
                            onChange={id => setInstitutionId(id)}
                            getOptionLabel={inst => inst.name}
                            clearable={false}
                            sx={{ minWidth: 220 }}
                        />
                    )}
                    <Button data-help="payroll.salaries.assign" variant="contained" color="primary" onClick={handleAdd} disabled={!institutionId}>
                        Assign Salary
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} data-help-section="payroll.salaries.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.salaries.col.employee">Employee</TableCell>
                            <TableCell data-help="payroll.salaries.col.effectiveFrom">Effective From</TableCell>
                            <TableCell align="right" data-help="payroll.salaries.col.earnings">Total Earnings</TableCell>
                            <TableCell align="right" data-help="payroll.salaries.col.deductions">Total Deductions</TableCell>
                            <TableCell align="right" data-help="payroll.salaries.col.net">Net Salary</TableCell>
                            <TableCell data-help="payroll.salaries.col.status">Status</TableCell>
                            <TableCell align="right" data-help="payroll.salaries.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {salaries.map((salary) => {
                            const totals = getTotalSalary(salary);
                            return (
                                <TableRow key={salary.id}>
                                    <TableCell>{salary.employeeName || displayUsername(salary.userName)}</TableCell>
                                    <TableCell>{salary.effectiveFrom ? formatDate(salary.effectiveFrom) : '-'}</TableCell>
                                    <TableCell align="right">{formatMoney(totals.earnings)}</TableCell>
                                    <TableCell align="right">{formatMoney(totals.deductions)}</TableCell>
                                    <TableCell align="right">{formatMoney(totals.net)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={salary.isActive ? 'Active' : 'Inactive'}
                                            color={salary.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton data-help="payroll.salaries.action.edit" onClick={() => handleEdit(salary)} size="small" aria-label="edit">
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {salaries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">No employee salaries assigned</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <EmployeeSalaryForm
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={handleSaved}
                salary={editingSalary}
                components={components}
            />
        </Box>
    );
};

export default EmployeeSalaryList;
